import React, { useState } from "react";
import styled from "styled-components";
import * as Yup from "yup";
import { Theme } from "@literati/public-ui-shared-react";
import LegacyThemeProvider from "components/Layout/DefaultThemeProvider";
import { Subscription } from "lib/api/subscription";
import GuidedSignUpLayoutRadio from "components/GuidedSignUp/GuidedSignUpLayoutRadio";
import StepLayout, {
  StepLayoutClassName,
  StepLayoutProps,
} from "components/Step/StepLayout";
import {
  getContentfulQuizStep,
  gsuSubmitAnswersDefault,
} from "components/Step/stepUtil";
import {
  RelationshipKey as Key,
  RelationshipSubmit,
  RelationshipValues,
  RelationshipValuesPartial,
} from "components/Step/stepTypes";

export const VALIDATION_SCHEMA = Yup.object().shape({
  [Key.RELATIONSHIP]: Yup.string().required("Required."),
});

export const INITIAL_VALUES: RelationshipValues = {
  [Key.KIND]: "relationship",
  [Key.RELATIONSHIP]: "",
} as const;

export function getRelationshipStep(contentfulQuizModel?: any) {
  const step = getContentfulQuizStep(
    contentfulQuizModel,
    "reader-relationship"
  );

  return step;
}

const StyledGuidedSignUpLayoutRadio = styled(GuidedSignUpLayoutRadio)`
  padding: 2rem 0;
`;

export type StepLayoutPropsRelationship = Omit<
  StepLayoutProps<RelationshipValues>,
  "asForm" | "children" | "formId"
>;

export interface StepRelationshipProps extends StepLayoutPropsRelationship {
  childName?: string;
  contentfulQuizModelParsed?: any;
  initialValues?: RelationshipValuesPartial;
  suppressGuidedSignUpSubmission?: boolean;
  onSubmit: RelationshipSubmit;
  subscription?: Subscription;
}

export const StepRelationship = ({
  childName,
  contentfulQuizModelParsed,
  initialValues: initialValuesProp,
  suppressGuidedSignUpSubmission = false,
  onSubmit,
  subscription,
  ...stepLayoutProps
}: StepRelationshipProps) => {
  const [initialValues] = useState<RelationshipValues>({
    ...INITIAL_VALUES,
    ...initialValuesProp,
  });

  const [step] = useState(getRelationshipStep(contentfulQuizModelParsed));

  const handleSubmit: RelationshipSubmit = async (values, formikHelpers) => {
    if (suppressGuidedSignUpSubmission) {
      return onSubmit?.(values, formikHelpers);
    }

    return await gsuSubmitAnswersDefault({
      contentfulQuizModel: contentfulQuizModelParsed,
      formikHelpers,
      /**
       * We put this step before dummy account creation for simplicity (as the user can return to this step).
       * The initial answer is submitted after dummy account creation. We ignore a failed API call and move forward.
       */
      onError: () => onSubmit?.(values, formikHelpers),
      onSubmit,
      step,
      subscription,
      values,
    });
  };

  return (
    <Theme>
      <StepLayout<RelationshipValues>
        asForm
        bgColor="white"
        css={`
          .${StepLayoutClassName.CONTENT_SPACING} {
            width: 100vw;
          }
        `}
        disableContentSpacing
        formId="activation-step-relationship"
        formikProps={{
          initialValues,
          onSubmit: handleSubmit,
          validationSchema: VALIDATION_SCHEMA,
        }}
        {...stepLayoutProps}
      >
        <LegacyThemeProvider>
          <StyledGuidedSignUpLayoutRadio step={step} childName={childName} />
        </LegacyThemeProvider>
      </StepLayout>
    </Theme>
  );
};

export default StepRelationship;
